import Phaser from 'phaser';
import { Organelle, OrganelleConfig } from './Organelle';

export interface NucleusConfig extends OrganelleConfig {
  // Nucleus-specific properties
  radius: number;
  chromatinDensity: number; // 0-1, how dense the chromatin appears
  nucleolusVisible: boolean;
  nucleolusColor: number;
}

export class Nucleus extends Organelle {
  private radius: number;
  private chromatinParticles: Phaser.GameObjects.Graphics[] = [];
  private nucleolus: Phaser.GameObjects.Graphics | null = null;
  private outerMembrane: Phaser.GameObjects.Graphics | null = null;
  private pulseAnimation: Phaser.Tweens.Tween | null = null;
  
  // Nucleus-specific defaults
  private static readonly DEFAULT_NUCLEUS_CONFIG: Partial<NucleusConfig> = {
    color: 0xE6ECF0, // Light blue-gray
    alpha: 0.9,
    outline: true,
    outlineColor: 0x3C4F65, // Darker blue
    type: 'nucleus',
    movementDelay: 0.05, // Extremely low value for very tight binding to cell center
    wobbleAmount: 0.1, // Subtle wobble
    maintainPosition: true, // Keep nucleus centered
    radius: 40,
    chromatinDensity: 0.5,
    nucleolusVisible: true,
    nucleolusColor: 0x705B91, // Purple
    depth: 80 // Below mitochondria and other organelles
  };
  
  constructor(scene: Phaser.Scene, x: number, y: number, config?: Partial<NucleusConfig>) {
    // Merge nucleus defaults with base defaults
    const nucleusConfig = {
      ...Nucleus.DEFAULT_NUCLEUS_CONFIG,
      ...config
    };
    
    super(scene, x, y, nucleusConfig);
    
    // Initialize all properties AFTER the super() call to ensure they exist when draw() is called
    this.radius = (this.config as NucleusConfig).radius || 40;
    this.chromatinParticles = []; // Explicitly initialize again
    
    // Create nucleus components with a small delay to ensure the base class is fully initialized
    // We need to check if this.scene still exists in the callback since the object might be destroyed
    try {
      if (this.scene) {
        this.scene.time.delayedCall(1, () => {
          // Safety check - if scene is gone, don't proceed
          if (!this.scene) {
            console.warn('Cannot initialize nucleus: scene reference is missing');
            return;
          }
          
          try {
            // Create nucleus components
            this.outerMembrane = scene.add.graphics();
            this.nucleolus = scene.add.graphics();
            
            // Add components to container (order matters for rendering)
            this.add(this.outerMembrane);
            this.add(this.nucleolus);
            
            // Draw immediately
            this.draw();
            
            // Note: Depth is now handled through config.depth in the parent class
            // No need for explicit setDepth call here
            
            // Create chromatin after the initial draw, but only if not minimal detail
            if ((this.config as NucleusConfig).detailLevel !== 'minimal') {
              if (this.scene) {
                this.scene.time.delayedCall(10, () => {
                  // One last safety check before creating chromatin
                  if (this.scene) {
                    this.createChromatin();
                  }
                });
              }
            }
          } catch (error) {
            console.error('Error in nucleus delayed initialization:', error);
          }
        });
      }
    } catch (error) {
      console.error('Error setting up nucleus initialization:', error);
    }
  }
  
  protected draw(): void {
    // Safety check - if scene is unavailable, don't try to draw
    if (!this.scene) {
      console.warn('Cannot draw nucleus: scene reference is missing');
      return;
    }
    
    // Clear all graphics
    this.mainShape.clear();
    
    // Safety check - if components aren't initialized yet, initialize them
    if (!this.outerMembrane || !this.nucleolus) {
      try {
        this.outerMembrane = this.scene.add.graphics();
        this.nucleolus = this.scene.add.graphics();
        
        // Add components to container
        this.add(this.outerMembrane);
        this.add(this.nucleolus);
      } catch (error) {
        console.error('Error creating nucleus components:', error);
        return; // Exit early if we can't create components
      }
    }
    
    this.outerMembrane.clear();
    this.nucleolus.clear();
    
    const config = this.config as NucleusConfig;
    const detailLevel = config.detailLevel;
    
    // Special handling for minimal detail level (remote cells in multiplayer)
    if (detailLevel === 'minimal') {
        // Make remote nuclei slightly smaller
        const radius = 38; // Reduced from 45 to 38 for remote cells

        // Calculate visibility based on distance to player (if in multiplayer)
        let baseAlpha = 0.4; // Increased default alpha for better visibility
        if (this.scene.scene.key === 'MultiplayerGameScene') {
            // Get player cell position - safely check for multiplayer scene
            const playerCell = (this.scene as any).playerCell;
            if (playerCell) {
                const distanceToPlayer = Phaser.Math.Distance.Between(
                    playerCell.x, playerCell.y,
                    this.parentContainer?.x || 0, 
                    this.parentContainer?.y || 0
                );
                // Adjust visibility based on distance (closer = more visible)
                baseAlpha = Math.max(0.3, Math.min(0.5, 1 - (distanceToPlayer / 1000)));
            }
        }

        // Also adjust based on camera zoom
        const zoomLevel = this.scene.cameras.main.zoom;
        const zoomAdjustedAlpha = Math.min(baseAlpha, 0.3 + (1 / zoomLevel * 0.1));
        
        this.mainShape.clear();
        
        // Add a very subtle glow effect - adjust alpha based on distance/zoom
        const glowColor = 0x4488FF; // Same blue as membrane
        this.mainShape.fillStyle(glowColor, zoomAdjustedAlpha * 0.3);
        this.mainShape.fillCircle(0, 0, radius * 1.1);
        
        // Main nucleus body - slightly transparent blue
        this.mainShape.fillStyle(0x4488FF, zoomAdjustedAlpha * 0.8);
        this.mainShape.fillCircle(0, 0, radius);
        
        // Add a subtle inner gradient
        this.mainShape.fillStyle(0x88AAFF, zoomAdjustedAlpha * 0.6);
        this.mainShape.fillCircle(0, 0, radius * 0.7);
        
        // Clear outline with thinner line and higher alpha for better visibility
        this.mainShape.lineStyle(1.5, 0x4488FF, Math.min(0.9, zoomAdjustedAlpha * 2));
        this.mainShape.strokeCircle(0, 0, radius);

        // Add pulse animation if not already created
        if (!this.pulseAnimation && this.scene) {
            this.pulseAnimation = this.scene.tweens.add({
                targets: this.mainShape,
                scaleX: 1.05,
                scaleY: 1.05,
                duration: 2000,
                yoyo: true,
                repeat: -1,
                ease: 'Sine.easeInOut'
            });
        }

        // Add movement trail effect if cell is moving fast
        const cell = this.parentContainer as any;
        if (cell?.velocity && cell.velocity.length() > 5) {
            const trail = this.scene.add.graphics();
            trail.fillStyle(0x4488FF, zoomAdjustedAlpha * 0.1);
            trail.fillCircle(0, 0, radius);
            this.add(trail); // Add to nucleus container
            
            this.scene.tweens.add({
                targets: trail,
                alpha: 0,
                duration: 500,
                onComplete: () => trail.destroy()
            });
        }
        
        return;
    }
    
    // Draw main nucleus body (light fill with texture)
    this.mainShape.fillStyle(config.color, config.alpha);
    this.mainShape.fillCircle(0, 0, this.radius);
    
    // Draw nuclear membrane (double membrane for high detail)
    if (detailLevel === 'high') {
      // Outer membrane
      this.outerMembrane.lineStyle(3, config.outlineColor, 0.9);
      this.outerMembrane.strokeCircle(0, 0, this.radius);
      
      // Inner membrane (slightly offset)
      this.outerMembrane.lineStyle(2, config.outlineColor, 0.7);
      this.outerMembrane.strokeCircle(0, 0, this.radius - 4);
    } else {
      // Simpler single membrane for medium/low detail
      const lineWidth = detailLevel === 'low' ? 2 : 3;
      this.outerMembrane.lineStyle(lineWidth, config.outlineColor, 0.9);
      this.outerMembrane.strokeCircle(0, 0, this.radius);
    }
    
    // Draw nucleolus if enabled
    if (config.nucleolusVisible) {
      const nucleolusRadius = this.radius * 0.3;
      
      // Position nucleolus slightly off-center
      const offsetX = this.radius * 0.2;
      const offsetY = this.radius * 0.1;
      
      this.nucleolus.fillStyle(config.nucleolusColor, 0.9);
      this.nucleolus.fillCircle(offsetX, offsetY, nucleolusRadius);
      
      // Add detail to nucleolus in high detail mode
      if (detailLevel === 'high') {
        this.nucleolus.lineStyle(1, 0x000000, 0.3);
        this.nucleolus.strokeCircle(offsetX, offsetY, nucleolusRadius);
      }
    }
    
    // Safety check for chromatin particles
    if (this.chromatinParticles && this.chromatinParticles.length > 0) {
      this.updateChromatinVisibility();
    }
  }
  
  private createChromatin(): void {
    // Safety check - if scene is unavailable, don't try to create chromatin
    if (!this.scene) {
      console.warn('Cannot create chromatin: scene reference is missing');
      return;
    }
    
    // Safety check - don't recreate chromatin if already created
    if (this.chromatinParticles.length > 0) {
      return;
    }
    
    // Don't create chromatin for minimal detail level
    if ((this.config as NucleusConfig).detailLevel === 'minimal') {
      return;
    }
    
    try {
      const config = this.config as NucleusConfig;
      const particleCount = Math.floor(15 + (config.chromatinDensity * 25)); // 15-40 particles
      
      // Create chromatin particles
      for (let i = 0; i < particleCount; i++) {
        const particle = this.scene.add.graphics();
        
        // Random position within nucleus
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * this.radius * 0.8; // Keep within 80% of radius
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;
        
        // Random size
        const size = 1 + Math.random() * 3;
        
        // Random color (darker variations of nucleus color)
        const colorVariation = Phaser.Display.Color.HSLToColor(
          Math.random() * 0.1,
          0.3 + Math.random() * 0.4,
          0.2 + Math.random() * 0.3
        ).color;
        
        // Draw particle
        particle.fillStyle(colorVariation, 0.7);
        particle.fillCircle(x, y, size);
        
        // Add to container and track
        this.add(particle);
        this.chromatinParticles.push(particle);
      }
      
      // Make sure chromatin visibility is updated
      this.updateChromatinVisibility();
    } catch (error) {
      console.error('Error creating chromatin particles:', error);
    }
  }
  
  private updateChromatinVisibility(): void {
    // Safety check for scene and chromatin
    if (!this.scene || 
        !this.chromatinParticles || 
        this.chromatinParticles.length === 0 || 
        (this.config as NucleusConfig).detailLevel === 'minimal') {
      return;
    }
    
    const detailLevel = (this.config as NucleusConfig).detailLevel;
    
    // Hide chromatin in minimal and low detail modes, show in medium/high
    const visible = (detailLevel !== 'low' && detailLevel !== 'minimal');
    
    try {
      this.chromatinParticles.forEach(particle => {
        particle.visible = visible;
      });
    } catch (error) {
      console.error('Error updating chromatin visibility:', error);
    }
  }
  
  // Override update to ensure nucleus stays at center and doesn't move
  update(delta: number, cellX: number, cellY: number, cellVelocity?: Phaser.Math.Vector2): void {
    // Call parent update for basic functionality 
    super.update(delta, cellX, cellY, cellVelocity);
    
    // Force position to center (0,0) - the nucleus should never move
    this.setPosition(0, 0);
    
    // Keep target position at center too
    this.targetPosition.set(0, 0);
    
    // Reset velocity to prevent any movement
    this.velocity.reset();
  }
  
  private updateChromatinPositions(): void {
    // Safety check - if scene is unavailable or no chromatin particles, exit
    if (!this.scene || !this.chromatinParticles || this.chromatinParticles.length === 0) {
      return;
    }
    
    // Subtle movement of chromatin to simulate biological activity
    this.chromatinParticles.forEach(particle => {
      // Get current position
      const currentX = particle.x;
      const currentY = particle.y;
      
      // Calculate distance from center
      const distance = Math.sqrt(currentX * currentX + currentY * currentY);
      
      // Only move if not too close to edge
      if (distance < this.radius * 0.7) {
        // Random small movement
        const moveX = (Math.random() - 0.5) * 2;
        const moveY = (Math.random() - 0.5) * 2;
        
        // Apply movement
        particle.x += moveX;
        particle.y += moveY;
      } else {
        // Move slightly toward center if too close to edge
        const angle = Math.atan2(currentY, currentX);
        particle.x -= Math.cos(angle) * 0.5;
        particle.y -= Math.sin(angle) * 0.5;
      }
    });
  }
  
  // Get nucleus-specific state for networking
  getState(): any {
    const baseState = super.getState();
    return {
      ...baseState,
      radius: this.radius,
      chromatinDensity: (this.config as NucleusConfig).chromatinDensity,
      nucleolusVisible: (this.config as NucleusConfig).nucleolusVisible
    };
  }

  destroy(fromScene?: boolean): void {
    // Stop and destroy pulse animation if it exists
    if (this.pulseAnimation) {
      this.pulseAnimation.stop();
      this.pulseAnimation.remove();
      this.pulseAnimation = null;
    }

    // Call parent destroy method
    super.destroy(fromScene);
  }
} 